import { useCallback, useState } from "react";
import { ChangeAdminStatusRequest, ChangeOrgApprovalGroupRequest, CollectionServerUser, DeleteUserRequest, MemberRekeyAndSetRecoveryRequest, SubsumeRequest } from "@preveil-api";
import { Account, PendingGroupUpdatesByUser, RequestTypes, usePostUsersFindMutation, UserRequest, ApprovalGroup, isSameUser, ResolvableOrgUser } from "src/common";
import _ from "lodash";

export function useGetPopulatedRequests(current_account: Account) {
  const [error, setError] = useState<unknown>();
  const [data, setData] = useState<{
    requests: UserRequest[],
    invalid_requests_count: number,
    pending_group_updates: PendingGroupUpdatesByUser,
    is_history: boolean,
  }>();
  const [findUsers] = usePostUsersFindMutation();

  async function getPopulatedRequests(
    requests: UserRequest[],
    org_users: ResolvableOrgUser[],
    approval_group: ApprovalGroup[],
    is_history: boolean,
  ) {
    let users: UserRequest[] | undefined;
    let user_ids = Array<string>();
    const pending_group_updates: PendingGroupUpdatesByUser = {};
    requests.forEach((request) => {
      try {
        if (!!request.requester_id) {
          user_ids = user_ids.concat(request.requester_id);
        }
        const request_info = populateRequestInfo(request, org_users, approval_group);
        user_ids = user_ids.concat(request_info.external_user_ids);
        if (request.type === RequestTypes.member_rekey_and_set_approval_group || request.type === RequestTypes.delete_user || request.type === RequestTypes.change_admin_status) {
          const request_type = request.type;
          request_info.for_user_ids.forEach((raw_user_id: string) => {
            const key = raw_user_id.toLowerCase();
            if (!pending_group_updates[key]) {
              pending_group_updates[key] = [{ request_id: request.request_id, request_type }];
            } else {
              pending_group_updates[key].push({ request_id: request.request_id, request_type });
            }
          });
        }
        return request;
      } catch (error) {
        setError(error);
        request.invalid = true;
      }
    });
    user_ids = _.uniqBy(user_ids, (id) => id);
    if (user_ids.length > 0) {
      users = await getUsers(
        user_ids,
        requests.filter((request) => !request.invalid),
      );
    } else {
      users = requests.filter((request) => !request.invalid);
    }
    const invalid_requests_count = requests.filter((request) => !!request.invalid).length;
    if (!!users) {
      setData({ requests: users, invalid_requests_count, pending_group_updates, is_history });
    }
  }

  /* Description: Gets user objects from the backend given a list of user ids and populates the request objects
  with those users. */
  async function getUsers(
    user_ids: string[],
    user_requests: UserRequest[],
  ): Promise<UserRequest[] | undefined> {
    if (!!current_account) {
      return await findUsers({
        account_ids: Account.getAccountIdentifiers(current_account),
        body: { spec: user_ids.map((id) => ({ user_id: id, key_version: -1 })) },
      })
        .unwrap()
        .then(({ users }) => {
          const users_map = users.reduce((accumulator: { [key: string]: CollectionServerUser }, user) => {
            accumulator[user.user_id.toLowerCase()] = user;
            return accumulator;
          }, {});
          const updated_admin_requests = user_requests.map((req) => {
            if (!!req.parsed_payload.for_users) {
              req.for_users = req.parsed_payload.for_users.map((for_user) => {
                const user_id = !!for_user ? for_user.toLowerCase() : "";
                const u = users_map[user_id] || {};
                return { name: u?.display_name || null, address: user_id };
              });
            }
            return req;
          });
          return updated_admin_requests;
        })
        .catch((_msg: string) => {
          const updated_response = user_requests.map((request) => {
            if (!!request.parsed_payload.approvers) {
              request.approvers = request.parsed_payload.approvers.map(
                (approver: { user_id: string }) => ({ name: null, address: approver.user_id }),
              );
            }
            return request;
          });
          return updated_response;
        });
    }
    return undefined;
  }

  /* Description: Used to populate the request object depending on the request type. Also collects for_user_ids, raw
     payload ids captured before the org_users lookup. org_users is guaranteed complete by now, so a lookup miss
     means the user doesn't exist. */
  function populateRequestInfo(request: UserRequest, org_users: ResolvableOrgUser[],
    approval_group: ApprovalGroup[]): { request: UserRequest; external_user_ids: string[]; for_user_ids: string[] } {
    let external_user_ids = Array<string>();
    const for_user_ids = Array<string>();
    let user: ResolvableOrgUser | undefined;
    let new_group_id: string;
    const u = org_users.find(
      (org_user) => isSameUser(org_user.user_id, request.requester_id),
    );
    request.requester = { name: u?.display_name || null, address: u?.user_id || "" };
    switch (request.type) {
      case RequestTypes.member_rekey_and_set_approval_group:
      case RequestTypes.set_member_approval_group:
        (request.metadata as MemberRekeyAndSetRecoveryRequest).events.forEach((event) => {
          try {
            const event_payload = JSON.parse(event.payload);
            new_group_id = event_payload.data.id;
            !!event_payload.for_user_id && for_user_ids.push(event_payload.for_user_id);
            const user = org_users.find(
              (org_user) =>
                isSameUser(org_user.user_id, event_payload.for_user_id),
            );
            if (!!user) {
              request.for_users.push({ address: user.user_id, name: user.display_name });
            }
          } catch {
            setError("error parsing event payload");
          }
        });
        if (!!request.for_users && request.for_users.length > 0) {
          // request.current_group = request.for_users[0].recovery_group;
        } else {
          request.invalid = true;
        }
        break;
      case RequestTypes.change_org_approval_group_role:
        new_group_id = (request.metadata as ChangeOrgApprovalGroupRequest).group_id;
        request.current_group = approval_group.find(
          (ag) => ag.approval_group_id === request.current_group_id,
        );
        break;
      case RequestTypes.change_admin_status:
        const change_admin_status = request.parsed_payload.data as ChangeAdminStatusRequest;
        for_user_ids.push(change_admin_status.user_id);
        user = org_users.find(
          (org_user) =>
            isSameUser(org_user.user_id, change_admin_status.user_id),
        );
        if (!!user) {
          request.updated_role = change_admin_status.role;
          request.for_users.push({ address: user.user_id, name: user.display_name });
        } else {
          request.invalid = true;
        }
        break;
      case RequestTypes.subsume_account:
        const subsume = request.parsed_payload.data as SubsumeRequest;
        external_user_ids = external_user_ids.concat(subsume.subsume_user_id);
        request.parsed_payload.for_users = [subsume.subsume_user_id];
        request.setOrgDetails({
          loaded: true,
          org_id: current_account?.org_info?.org_id || "",
          org_name: current_account?.org_info?.org_name || "",
        });
        break;
      case RequestTypes.delete_user:
        const delete_user = request.parsed_payload.data as DeleteUserRequest;
        for_user_ids.push(delete_user.user_id);
        user = org_users.find(
          (org_user) =>
            isSameUser(org_user.user_id, delete_user.user_id),
        );
        if (!!user) {
          request.for_users.push({ address: user.user_id, name: user.display_name });
        } else {
          request.invalid = true;
        }
        break;
    }
    const associated_ag = approval_group.find(
      (ag) => ag.approval_group_id === new_group_id,
    );
    if (associated_ag) {
      request.approval_group_name = associated_ag.name;
      request.optionals_required = associated_ag.required_approvers;
      request.approvers = associated_ag.approvers;
    }
    return { request, external_user_ids, for_user_ids };
  }

  const refreshPopulatedRequests = useCallback(
    (requests: UserRequest[], org_users: ResolvableOrgUser[], approval_group: ApprovalGroup[], is_history: boolean) => {
      getPopulatedRequests(requests, org_users, approval_group, is_history);
    }, []);

  // Description: Clears out the previous result so a new fetch in progress can't be mistaken for stale leftover data.
  const clearPopulatedRequests = useCallback(() => {
    setData(undefined);
  }, []);

  return {
    data,
    error,
    refreshPopulatedRequests,
    clearPopulatedRequests
  };
}

